import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getExternalServiceAssignee, useGetAppsByIds } from "@web-app/api";
import { SystemApp, Workspace, WorkspaceConnection } from "@web-app/types";

import { Icon } from "../Icon";

export default function SendToMenu({
  close,
  connectedWorkspace,
  filteredWorkspaceConnections,
  onShare,
  openExternalProviderModal,
  setIsShareModalVisible,
  workspace,
  entity,
}: {
  close: () => void;
  connectedWorkspace?: Workspace;
  entity: {
    externalAssigneeId: string;
    id: string;
    numOfOrders?: number;
    type: "" | "ORDER" | "P2P_ORDER" | "ROUTE";
  };
  filteredWorkspaceConnections?: WorkspaceConnection[];
  onShare?: (workspaceId: string) => void;
  openExternalProviderModal: (provider: string) => void;
  setIsShareModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  workspace?: Workspace;
}) {
  const { data: apps } = useGetAppsByIds([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(
    null,
  );
  const btnRef = useRef<HTMLButtonElement>(null);
  const hasOpenedRef = useRef(false);

  const PANEL_WIDTH = 160;

  const toggleOpen = () => {
    hasOpenedRef.current = true;
    getProviders();
    setOpen((prev) => {
      const next = !prev;
      if (next && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        // Prefer opening to the left of the trigger; fall back to the right.
        let left = rect.left - PANEL_WIDTH - 4;
        if (left < 4) {
          left = rect.right + 4;
        }
        setCoords({ left, top: rect.top });
      }
      return next;
    });
  };

  // Re-compute providers when apps data refreshes after submenu was opened
  useEffect(() => {
    if (hasOpenedRef.current) {
      getProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps]);

  const getExternalAssignee = async () => {
    const externalAssigneeType =
      entity.type === "P2P_ORDER" || entity.type === "ORDER"
        ? "order"
        : "route";
    const resp = await getExternalServiceAssignee(
      externalAssigneeType,
      entity.id,
    );
    return resp;
  };

  const getProviders = async () => {
    const externalAssignee = await getExternalAssignee();
    let providers: string[] = [];
    const { type, externalAssigneeId, numOfOrders } = entity;
    if (
      externalAssigneeId ||
      !apps ||
      externalAssignee?.remark === "accepted"
    ) {
      return;
    }

    if (
      externalAssignee &&
      ["uber", "doordash", "roadie", "onfleet"].includes(externalAssignee.type)
    ) {
      return;
    }

    if (type === "P2P_ORDER") {
      providers = apps
        .map((app) => {
          if (app.systemApp === SystemApp.UBER && app.isActive) {
            return "uber";
          }
          if (app.systemApp === SystemApp.DOORDASH && app.isActive) {
            return "doordash";
          }
          if (app.systemApp === SystemApp.ROADIE && app.isActive) {
            return "roadie";
          }
          if (app.systemApp === SystemApp.EXPEDITE && app.isActive) {
            return "expedite";
          }
          if (app.systemApp === SystemApp.ONFLEET && app.isActive) {
            return "onfleet";
          }
          return null;
        })
        .filter((provider) => provider !== null);
    } else if (type === "ORDER") {
      providers = apps
        .map((app) => {
          if (app.systemApp === SystemApp.ONFLEET && app.isActive) {
            return "onfleet";
          }
          return null;
        })
        .filter((provider) => provider !== null);
    } else if (type === "ROUTE") {
      providers = apps
        .map((app) => {
          if (
            app.systemApp === SystemApp.UBER &&
            app.isActive &&
            numOfOrders === 2
          ) {
            return "uber";
          }
          if (app.systemApp === SystemApp.ROADIE && app.isActive) {
            return "roadie";
          }
          if (app.systemApp === SystemApp.EXPEDITE && app.isActive) {
            return "expedite";
          }
          if (app.systemApp === SystemApp.ONFLEET && app.isActive) {
            return "onfleet";
          }
          return null;
        })
        .filter((provider) => provider !== null);
    }
    setProviders(providers);
  };

  const menuItemIconClasses = "w-[20px] lg:w-[25px] h-[20px] lg:h-[25px]";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={clsx(
          "px-2 py-1.5 hover:bg-gray-200 w-full text-gray-700 text-xs lg:text-sm text-left flex items-center",
        )}
        onClick={toggleOpen}
      >
        <span>Send To</span>
        <Icon name="chevronRight" className="h-3 ml-auto" />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            style={{
              left: coords.left,
              position: "fixed",
              top: coords.top,
              width: PANEL_WIDTH,
            }}
            className="z-[9999] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            {onShare && (
              <div className={clsx("pb-2", "border-t border-gray-200")}>
                <ExternalWorkspaceSendToOptions
                  workspace={workspace}
                  connectedWorkspace={connectedWorkspace}
                  setIsShareModalVisible={setIsShareModalVisible}
                  filteredWorkspaceConnections={filteredWorkspaceConnections}
                />
              </div>
            )}
            {providers.length > 0 && (
              <div>
                <span className="text-[10px] lg:text-xs text-gray-400 pb-1 p-2 block">
                  External
                </span>
                {providers.includes("uber") && (
                  <ContextMenuItem
                    activateOnPointerDown
                    onClick={() => {
                      close();
                      openExternalProviderModal("uber");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/uber.png"
                        alt={""}
                        className={menuItemIconClasses}
                      />
                      Uber
                    </div>
                  </ContextMenuItem>
                )}
                {providers.includes("doordash") && (
                  <ContextMenuItem
                    activateOnPointerDown
                    onClick={() => {
                      close();
                      openExternalProviderModal("doordash");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/doordash.png"
                        alt={""}
                        className={menuItemIconClasses}
                      />
                      Doordash
                    </div>
                  </ContextMenuItem>
                )}
                {providers.includes("roadie") && (
                  <ContextMenuItem
                    activateOnPointerDown
                    onClick={() => {
                      close();
                      openExternalProviderModal("roadie");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/roadie.png"
                        alt={""}
                        className={menuItemIconClasses}
                      />
                      Roadie
                    </div>
                  </ContextMenuItem>
                )}
                {providers.includes("expedite") && (
                  <ContextMenuItem
                    activateOnPointerDown
                    onClick={() => {
                      close();
                      openExternalProviderModal("expedite");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/expedite.png"
                        alt={""}
                        className={menuItemIconClasses}
                      />
                      Expedite
                    </div>
                  </ContextMenuItem>
                )}
                {providers.includes("onfleet") && (
                  <ContextMenuItem
                    activateOnPointerDown
                    onClick={() => {
                      close();
                      openExternalProviderModal("onfleet");
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/onfleet.png"
                        alt={""}
                        className={menuItemIconClasses}
                      />
                      Onfleet
                    </div>
                  </ContextMenuItem>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function ExternalWorkspaceSendToOptions({
  connectedWorkspace,
  filteredWorkspaceConnections,
  setIsShareModalVisible,
  workspace,
}: {
  connectedWorkspace?: Workspace;
  filteredWorkspaceConnections?: WorkspaceConnection[];
  setIsShareModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  workspace?: Workspace;
}) {
  if ((filteredWorkspaceConnections || []).length) {
    return (
      <>
        <span className="text-[10px] lg:text-xs text-gray-400 p-2 pb-1 block">
          Internal
        </span>
        <ContextMenuItem
          activateOnPointerDown
          onClick={() => setIsShareModalVisible(true)}
        >
          {connectedWorkspace ? (
            <span className="flex justify-start items-center">
              <Icon
                name="circleCheck"
                className="h-3 lg:h-4 text-green-400 text-xs lg:text-sm mr-2"
              />
              <span className="text-xs lg:text-sm relative max-w-[100px] lg:max-w-[150px] overflow-hidden text-ellipsis">
                {connectedWorkspace.name}
              </span>
            </span>
          ) : (
            "Select Workspace"
          )}
        </ContextMenuItem>
      </>
    );
  } else {
    return (
      <>
        <span className="text-[10px] lg:text-xs text-gray-400 p-2 pb-1 block">
          Internal
        </span>
        <ContextMenuItem onClick={() => {}} disabled>
          {workspace && (
            <>
              <Icon
                name="fileExport"
                className="h-3 lg:h-4 text-green-400 ml-auto mr-2"
              />
              {workspace.name}
            </>
          )}
        </ContextMenuItem>
      </>
    );
  }
}

export const ContextMenuItem = React.forwardRef<
  HTMLSpanElement,
  {
    // When the item lives in a portal outside a HeadlessUI menu, that menu
    // closes on the click's pointerdown and unmounts the item before the
    // click fires. Activating on pointerdown runs the handler first.
    activateOnPointerDown?: boolean;
    children: React.ReactNode;
    disabled?: boolean;
    onClick: () => void;
  }
>((props, ref) => (
  <span
    ref={ref}
    className={clsx(
      "block cursor-pointer items-center p-2 lg:py-1 py-2 text-xs lg:text-sm font-normal text-gray-600 hover:bg-gray-200 leading-none whitespace-nowrap w-[150px] max-w-full overflow-hidden text-ellipsis",
      props.disabled && "cursor-default opacity-60 hover:bg-transparent",
    )}
    onPointerDown={
      props.disabled || !props.activateOnPointerDown ? undefined : props.onClick
    }
    onClick={
      props.disabled || props.activateOnPointerDown ? undefined : props.onClick
    }
  >
    {props.children}
  </span>
));

ContextMenuItem.displayName = "ContextMenuItem";
